import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage } from "@/App";
import { cvApi } from "@/lib/cv";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BackBar from "@/components/BackBar";
import {
  Crown, Infinity as InfinityIcon, ShieldCheck, Sparkles, CheckCircle2, ArrowLeft,
  Languages, Star, FileText, MessageSquare, DollarSign, Wand2, Lock,
} from "lucide-react";

const benefits = (t) => [
  { Icon: ShieldCheck,     title: t("✅ توافق مع أنظمة ATS", "✅ ATS Resume Optimization"),
    body:  t("اجتز أنظمة الفرز التلقائي التي تستخدمها معظم الشركات الأمريكية لتصفية السير قبل وصولها لأعين الموظف البشري — حتى ترتفع فرصتك في أن يراها صاحب العمل فعلاً.",
             "Pass the automated screening systems most US companies use to filter resumes before a human ever sees them — so your resume actually gets noticed by recruiters.") },
  { Icon: MessageSquare,   title: t("✅ خطاب تغطية احترافي", "✅ Professional Cover Letter"),
    body:  t("نولّد لك خطاب تغطية احترافي يُقدّمك بأسلوب يصنع انطباعاً قوياً من أول رسالة — ويرفع فرصك في الحصول على مقابلة عمل بدلاً من تجاهل طلبك.",
             "We generate a polished cover letter that introduces you professionally and makes a strong first impression — boosting your chance of getting interview calls instead of silence.") },
  { Icon: Wand2,           title: t("✅ مدرّب مهني بالذكاء الاصطناعي", "✅ AI Career Coach"),
    body:  t("مساعد ذكي شخصي يساعدك على بناء سيرة أقوى: يحسّن وصف خبراتك، يكتشف المهارات المهمة في مجالك، يقترح صياغة أفضل، ويقدّم لك توصيات مخصصة لحالتك.",
             "A personal AI assistant that helps you build a stronger resume: improves your experience descriptions, identifies important skills, suggests better wording, and gives personalized recommendations.") },
  { Icon: Languages,       title: t("✅ ترجمة إنجليزية أمريكية احترافية", "✅ Professional American Translation"),
    body:  t("نُعيد كتابة سيرتك بمعايير لغة الأعمال الأمريكية الحقيقية المناسبة لسوق العمل في الولايات المتحدة — وليست ترجمة حرفية. صياغة يفهمها أصحاب العمل ويتفاعلون معها.",
             "We rewrite your resume in real US business English suitable for the American job market — not a literal translation. Wording that US employers understand and respond to.") },
  { Icon: FileText,        title: t("✅ +50 قالب احترافي بتصاميم مختلفة فعلياً", "✅ 50+ Unique Resume Templates"),
    body:  t("اختر من بين أكثر من 50 تخطيطاً مختلفاً فعلياً — وليس نفس التصميم بألوان مختلفة. مع دعم الصورة الشخصية و10 ألوان احترافية لتختار ما يناسب مجالك.",
             "Choose from 50+ genuinely different layouts — not the same design in different colors. With profile picture support and 10 professional color themes to match your field.") },
  { Icon: DollarSign,      title: t("✅ دليل الرواتب الأمريكي", "✅ US Salary Guide"),
    body:  t("اعرف الراتب المتوسط في مهنتك حسب كل ولاية بناءً على بيانات BLS الأمريكية الرسمية — لتتفاوض مع صاحب العمل من موقع قوة وثقة، بدلاً من التخمين.",
             "Know the median salary for your profession in each US state based on official BLS data — so you negotiate from a position of strength and confidence, not guesswork.") },
];

export default function CVPremium() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    cvApi.pricing().then(setPricing).catch(() => {});
  }, []);

  const regular = pricing?.regular_price_usd ?? 13.99;
  const offer   = pricing?.offer_price_usd ?? 4.99;
  const saving  = pricing?.savings_pct ?? Math.round((1 - offer / regular) * 100);

  const checkout = () => {
    // Phase 2 hook-up: this will jump into the Stripe Checkout flow.
    toast.info(t(
      "سيتم تفعيل الشراء قريباً عبر Stripe Checkout الآمن — سنبلّغك فور توفّره.",
      "Secure Stripe Checkout will be activated shortly — we'll notify you the moment it goes live."
    ));
  };

  return (
    <DashboardLayout title={t("باقة النجاح المهني", "Career Success Package")}>
      <BackBar to="/cv" label_ar="مركز السيرة الذاتية" label_en="Resume Center"
        crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { to: "/cv", ar: "السيرة الذاتية", en: "Resume Center" }, { ar: "باقة النجاح", en: "Premium" }]} />
      <div className="space-y-8 max-w-5xl mx-auto" data-testid="cv-premium-page">

        {/* Hero */}
        <div className="relative glass-card rounded-3xl overflow-hidden p-6 lg:p-10 bg-luxury">
          <div className="absolute -top-40 -right-40 w-[480px] h-[480px] bg-yellow-500/25 blur-[140px] rounded-full" />
          <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] bg-amber-500/20 blur-[140px] rounded-full" />
          <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-6 items-center">
            <div>
              <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold mb-3">⭐⭐⭐⭐⭐ {t("الأكثر شعبية", "Most Popular")}</Badge>
              <h1 className="text-3xl lg:text-5xl font-bold text-luxury leading-tight mb-3">
                🚀 {t("استثمر في فرصة عمل أفضل", "Invest in a better job opportunity")}
              </h1>
              <p className="text-slate-300 text-base lg:text-lg mb-3">
                {t(
                  "تجربة مدرّب مهني احترافي شبيه بـ Resume.io و MyPerfectResume — لكن بسعر يستحق ودون اشتراك شهري.",
                  "A premium Career Coach experience like Resume.io & MyPerfectResume — without the monthly subscription."
                )}
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Badge className="bg-red-500/90 text-white">🔥 {t("عرض محدود", "Limited-Time Offer")}</Badge>
                <Badge className="bg-emerald-500/90 text-black font-bold">-{saving}% {t("خصم", "OFF")}</Badge>
                <Badge className="bg-slate-800 text-slate-200 inline-flex items-center gap-1"><InfinityIcon size={11} /> {t("مدى الحياة", "Lifetime")}</Badge>
              </div>
              <div className="flex items-baseline gap-3 mb-2">
                {/* Promotional period: show only the current offer price ($4.99). */}
                <span className="text-5xl lg:text-6xl font-bold text-yellow-400" data-testid="premium-offer-price">${offer.toFixed(2)}</span>
              </div>
              <p className="text-sm text-slate-300 mb-1">{t("دفعة واحدة فقط — لا اشتراك ، لا تجديد", "One-time payment — no subscription, no renewal")}</p>
              <p className="text-xs text-emerald-300 inline-flex items-center gap-1 mb-5"><ShieldCheck size={12} />{t("ضمان استرداد 7 أيام (بدون أسئلة)", "7-day money-back guarantee (no questions asked)")}</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={checkout} className="btn-primary px-6 py-6 text-base" data-testid="cv-premium-checkout-btn">
                  <Crown size={16} className="ml-2" />{t("احصل عليها الآن — $", "Get it now — $")}{offer.toFixed(2)}
                </Button>
                <Button onClick={() => navigate("/cv")} variant="outline" className="border-slate-700 text-slate-300 px-5">
                  <ArrowLeft size={14} className="ml-1" />{t("عودة", "Back")}
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-yellow-500/30 blur-3xl rounded-3xl" />
                <div className="relative bg-slate-900/80 border border-yellow-500/30 rounded-3xl p-6 backdrop-blur">
                  <div className="text-center">
                    <Crown className="mx-auto text-yellow-400" size={48} />
                    <p className="text-yellow-300 font-bold mt-3 text-lg">{pricing?.headline_ar && language === "ar" ? pricing.headline_ar : (pricing?.headline_en || "Career Success Package")}</p>
                    <p className="text-slate-300 text-sm mt-2">{pricing?.tagline_ar && language === "ar" ? pricing.tagline_ar : (pricing?.tagline_en || "")}</p>
                  </div>
                  <div className="mt-5 space-y-2">
                    {[
                      t("✅ +50 قالب احترافي مختلف فعلياً", "✅ 50+ Unique Templates"),
                      t("✅ مدرّب مهني بالذكاء الاصطناعي", "✅ AI Career Coach"),
                      t("✅ ترجمة أمريكية احترافية", "✅ Professional US Translation"),
                      t("✅ توافق مع أنظمة ATS", "✅ ATS Optimization"),
                      t("✅ مراجعة قبل التحميل", "✅ Pre-download Review"),
                      t("✅ خطاب تغطية + تحضير مقابلات + رواتب", "✅ Cover Letter + Interview + Salary"),
                      t("♾ وصول مدى الحياة", "♾ Lifetime access"),
                    ].map((line, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-200">
                        <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />{line}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits grid */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Sparkles className="text-yellow-500" size={22} />{t("ماذا يوجد داخل الباقة؟", "What's inside?")}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits(t).map((b, i) => (
              <div key={i} className="glass-card rounded-2xl p-5">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-700 flex items-center justify-center mb-3">
                  <b.Icon className="text-white" size={20} />
                </div>
                <h3 className="font-bold text-white mb-1">{b.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why $4.99 */}
        <div className="glass-card rounded-3xl p-6 lg:p-8 border border-yellow-500/30">
          <h2 className="text-xl font-bold text-white mb-3 inline-flex items-center gap-2">
            <Star className="text-yellow-500" />{t("لماذا 4.99 دولار فقط؟", "Why only $4.99?")}
          </h2>
          <p className="text-slate-300 text-sm leading-relaxed mb-3">
            {t(
              "نحن جاليتك — رسالتنا أن نوفّر لك أدوات أمريكية احترافية بسعر يستحق، وأنت تستثمر في وظيفة قد ترفع دخلك آلاف الدولارات سنوياً. لذا حدّدنا السعر بحيث يبقى في متناول الجميع.",
              "We are your community — our mission is to give you US-grade career tools at a price that respects your situation. A better resume can lift your income by thousands per year. So we priced this to be within everyone's reach."
            )}
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {[
              { ar: "🛡️ بدون اشتراك",                en: "🛡️ No subscription" },
              { ar: "♾ وصول مدى الحياة",             en: "♾ Lifetime access" },
              { ar: "↩️ ضمان استرداد 7 أيام",        en: "↩️ 7-day refund guarantee" },
            ].map((x, i) => (
              <div key={i} className="bg-slate-900/50 rounded-xl px-4 py-3 text-sm text-slate-200 border border-slate-700">
                {language === "ar" ? x.ar : x.en}
              </div>
            ))}
          </div>
        </div>

        {/* FAQ short */}
        <div className="glass-card rounded-3xl p-6 lg:p-8">
          <h2 className="text-xl font-bold text-white mb-3 inline-flex items-center gap-2">
            <Lock className="text-yellow-500" />{t("أسئلة شائعة", "FAQ")}
          </h2>
          <div className="space-y-3 text-sm">
            {[
              { q: t("هل أحتاج لاشتراك شهري؟", "Do I need a monthly subscription?"),
                a: t("لا. الدفعة لمرة واحدة فقط ولن نحاسبك لاحقاً.", "No. One-time payment only — we will never charge you again.") },
              { q: t("هل سيرتي محفوظة إلى الأبد؟", "Will my resume be saved forever?"),
                a: t("نعم. يمكنك العودة وتعديلها وتنزيلها متى شئت.", "Yes. You can come back, edit and re-download any time.") },
              { q: t("هل القوالب مقبولة في الشركات الأمريكية؟", "Are templates accepted by US companies?"),
                a: t("نعم. صُممت بمعايير أمريكية — بدون صورة، بدون عمر، متوافقة مع ATS.", "Yes. Designed to US standards — no photo, no age, ATS-friendly.") },
              { q: t("ماذا لو لم تعجبني؟", "What if I'm not satisfied?"),
                a: t("استرداد كامل خلال 7 أيام بدون أسئلة.", "Full refund within 7 days, no questions asked.") },
            ].map((f, i) => (
              <details key={i} className="bg-slate-900/50 rounded-xl px-4 py-3 border border-slate-700">
                <summary className="text-slate-200 font-medium cursor-pointer">{f.q}</summary>
                <p className="text-slate-400 mt-2 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center glass-card rounded-3xl p-6 border-2 border-yellow-500/40">
          <Crown className="mx-auto text-yellow-400 mb-2" size={32} />
          <h2 className="text-2xl font-bold text-white mb-1">{t("جاهز لرفع فرصك؟", "Ready to boost your odds?")}</h2>
          <p className="text-slate-300 text-sm mb-4">{t("ابدأ الآن — يستغرق الأمر دقائق", "Get started — it takes just minutes")}</p>
          <Button onClick={checkout} className="btn-primary px-6 py-6 text-base" data-testid="cv-premium-checkout-bottom">
            <Crown size={16} className="ml-2" />{t("احصل عليها الآن — $", "Get it now — $")}{offer.toFixed(2)}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
